import { Warranty, NewWarrantyInput, User } from "./types";
import { fetchTeamMemberLookup } from "@/lib/entity-api";
import { useEntityList } from "@/hooks/useEntityList";
import { getApiUrl } from "@/lib/api-config";
import { useUser } from "@/contexts/UserContext";
import { useProject } from "@/components/ProjectSelector";

/**
 * Warranties data layer. WHICH warranties make up the list (search, sort, project, page, total) is the generic
 * entity query (hooks/useEntityList.ts -> GET /api/entities/warranty); the DERIVED data of the page being shown
 * comes from GET /api/warranties?ids=..., which returns each warranty with its names, attachment/comment counts (counted on the
 * server from the unified attachments/comments tables), related purchase
 * orders and the DERIVED ball in court; writes go through the generic entity
 * API. Nothing here counts or derives anything itself.
 */

export function mapApiRowToWarranty(row: any): Warranty {
  return {
    id: row.id,
    warrantyNumber: row.warranty_number,
    title: row.title ?? row.description ?? '',
    projectId: row.project_id,
    description: row.description ?? undefined,
    response: row.response ?? undefined,
    startDate: row.start_date,
    endDate: row.end_date,
    status: row.status,
    assignedTo: row.assigned_to ?? undefined,
    assignedToName: row.assigned_to_name ?? undefined,
    createdAt: row.created_at,
    responseDate: row.response_date ?? undefined,
    author: row.author_name || row.created_by || 'Unknown User',
    createdByType: row.created_by_type ?? 'company',
    attachmentCount: typeof row.attachment_count === 'number' ? row.attachment_count : 0,
    commentsCount: typeof row.comment_count === 'number' ? row.comment_count : 0,
    ballInCourt: row.ball_in_court ?? null,
    relatedPurchaseOrders: (row.related_purchase_orders ?? []).map((po: any) => ({
      id: po.id,
      poNumber: po.po_number,
      title: po.title ?? null,
      status: po.status,
    })),
  };
}

async function apiError(response: Response, fallback: string): Promise<Error> {
  const body = await response.json().catch(() => ({}));
  return new Error(body?.error || body?.message || fallback);
}

const DERIVED_CHUNK = 100;

/** The derived data (names, counters, related purchase orders, ball in court) of these warranties: one request per page. */
export async function fetchWarrantyDerived(ids: string[]): Promise<Warranty[]> {
  const out: Warranty[] = [];
  for (let i = 0; i < ids.length; i += DERIVED_CHUNK) {
    const response = await fetch(getApiUrl(`api/warranties?ids=${ids.slice(i, i + DERIVED_CHUNK).join(',')}`));
    if (!response.ok) throw await apiError(response, 'Failed to load warranties');
    out.push(...((await response.json()).data ?? []).map(mapApiRowToWarranty));
  }
  return out;
}

/** CSV rows of the warranties an export returned (the table's columns + names, ball in court and counters). */
export async function warrantyExportRows(
  exported: Array<Record<string, any>>,
  projectName: (projectId: string) => string,
  formatAssignedTo: (warranty: Warranty) => string
): Promise<Array<Record<string, unknown>>> {
  const derived = new Map((await fetchWarrantyDerived(exported.map((w) => w.id))).map((w) => [w.id, w] as const));
  return exported.flatMap((row) => {
    const w = derived.get(row.id);
    if (!w) return [];
    return [{
      warranty_number: w.warrantyNumber, title: w.title, project: projectName(w.projectId), description: w.description, response: w.response,
      status: w.status, assigned_to: formatAssignedTo(w), ball_in_court: w.ballInCourt, created_at: w.createdAt,
      response_date: w.responseDate, attachments: w.attachmentCount, comments: w.commentsCount,
    }];
  });
}

/** Server-side fields only: company, author and the warranty number are never sent. */
function writePayload(input: Partial<NewWarrantyInput>) {
  return {
    project_id: input.projectId,
    title: input.title,
    description: input.description || null,
    response: input.response || null,
    status: input.status,
    assigned_to: input.assignedTo || null,
  };
}

export async function createWarrantyRecord(input: NewWarrantyInput): Promise<{ id: string }> {
  const today = new Date();
  const response = await fetch(getApiUrl('api/entities/warranty'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...writePayload(input),
      start_date: today.toISOString().split('T')[0],
      end_date: new Date(today.getTime() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      created_by_type: 'company',
    }),
  });
  if (!response.ok) throw await apiError(response, 'Failed to create warranty');
  return (await response.json()).data;
}

export async function updateWarrantyRecord(id: string, input: Partial<NewWarrantyInput>): Promise<void> {
  const response = await fetch(getApiUrl(`api/entities/warranty/${id}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(writePayload(input)),
  });
  if (!response.ok) throw await apiError(response, 'Failed to update warranty');
}

/** Team members who can be assigned (admins and members of the company), from the people lookup. */
export async function fetchAssignableUsers(): Promise<User[]> {
  try {
    const members = await fetchTeamMemberLookup(['member', 'admin']);
    return members.map((m) => ({ id: m.id, first_name: m.first_name ?? undefined, last_name: m.last_name ?? undefined, email: m.email, type: 'user' as const }));
  } catch (error) {
    console.error('Failed to fetch company users:', error);
    return [];
  }
}

export function useWarranties() {
  const { companyId } = useUser();
  const { selectedProject } = useProject();
  const list = useEntityList<any, Warranty>({
    entityType: 'warranty',
    projectId: selectedProject,
    enabled: !!companyId,
    mode: 'pages',
    defaultSort: { columnKey: 'warrantyNumber', direction: 'asc', sortKey: 'warranty_number' },
    mapRow: mapApiRowToWarranty,
    // Only the page on screen gets its derived data (ball in court, related POs, counters, names).
    enrichPage: async (rows, items) => {
      const derived = new Map((await fetchWarrantyDerived(rows.map((r) => r.id))).map((w) => [w.id, w] as const));
      return items.map((item) => derived.get(item.id) ?? item);
    },
  });
  const { setItems, reload } = list;

  /** Creates through the API, then reads the new warranty's derived data and shows it. Throws on failure. */
  const addWarranty = async (input: NewWarrantyInput): Promise<Warranty | undefined> => {
    const { id } = await createWarrantyRecord(input);
    const [created] = await fetchWarrantyDerived([id]);
    reload(); // where it belongs depends on the server's order / search
    return created;
  };

  const updateWarranty = async (id: string, input: Partial<NewWarrantyInput>): Promise<Warranty | undefined> => {
    await updateWarrantyRecord(id, input);
    const [updated] = await fetchWarrantyDerived([id]);
    if (updated) setItems((prev) => prev.map((w) => (w.id === id ? updated : w)));
    return updated;
  };

  return { ...list, addWarranty, updateWarranty, refreshWarranties: reload };
}
